/**
 * Savings Tracker Service
 *
 * Tracks and calculates savings from subscription optimizations:
 * - Cancellations
 * - Downgrades
 * - Annual billing switches
 */

import { supabase } from './supabaseClient';

// ============================================================================
// SAVINGS CALCULATION
// ============================================================================

/**
 * Calculate active monthly savings
 * (Excludes expired annual savings after 12 months)
 */
export const calculateMonthlySavings = async (userId) => {
  console.log('💰 [calculateMonthlySavings] Starting calculation for user:', userId);

  try {
    const { data, error } = await supabase
      .from('savings_events')
      .select('*')
      .eq('user_id', userId);

    if (error) {
      console.error('❌ [calculateMonthlySavings] Database error:', error);
      throw error;
    }

    console.log(`📊 [calculateMonthlySavings] Found ${data?.length || 0} savings events`);
    if (data && data.length > 0) {
      console.log('📋 [calculateMonthlySavings] Events:', data);
    }

    const now = new Date();
    let monthlySavings = 0;

    data.forEach(event => {
      // For annual switches, only count if within 12-month window
      if (event.event_type === 'annual_switch') {
        const endDate = new Date(event.savings_end_date);
        if (endDate > now) {
          monthlySavings += parseFloat(event.monthly_savings);
          console.log(`  ✅ Annual switch: +$${event.monthly_savings}/mo`);
        } else {
          console.log(`  ⏰ Annual switch expired`);
        }
      } else {
        // Canceled and downgraded savings are ongoing
        monthlySavings += parseFloat(event.monthly_savings);
        console.log(`  ✅ ${event.event_type}: +$${event.monthly_savings}/mo`);
      }
    });

    console.log(`💵 [calculateMonthlySavings] Total monthly savings: $${monthlySavings.toFixed(2)}`);
    return parseFloat(monthlySavings.toFixed(2));
  } catch (error) {
    console.error('❌ [calculateMonthlySavings] Error:', error.message);
    if (error.message?.includes('relation') || error.message?.includes('does not exist')) {
      console.warn('⚠️  Database tables not created. Run add_ai_insights_tables.sql');
    }
    return 0;
  }
};

/**
 * Calculate lifetime total savings
 * Sum of all monthly savings from event date to now (minimum 1 month)
 */
export const calculateLifetimeSavings = async (userId) => {
  console.log('💰 [calculateLifetimeSavings] Starting calculation for user:', userId);

  try {
    // Get all savings events
    const { data: events, error } = await supabase
      .from('savings_events')
      .select('*')
      .eq('user_id', userId)
      .order('event_date', { ascending: true });

    if (error) throw error;

    console.log(`📊 [calculateLifetimeSavings] Found ${events?.length || 0} savings events`);

    const now = new Date();
    let lifetimeSavings = 0;

    events.forEach(event => {
      const eventDate = new Date(event.event_date);
      const monthlySavings = parseFloat(event.monthly_savings);

      if (event.event_type === 'annual_switch') {
        // Annual savings: count for up to 12 months from event date
        const endDate = new Date(event.savings_end_date);
        const actualEndDate = endDate < now ? endDate : now;

        // Calculate months between event date and end date (minimum 1)
        const monthsActive = Math.max(1,
          Math.ceil((actualEndDate - eventDate) / (1000 * 60 * 60 * 24 * 30))
        );

        lifetimeSavings += monthlySavings * monthsActive;
        console.log(`  ✅ Annual switch "${event.subscription_name}": $${monthlySavings}/mo × ${monthsActive} months = $${(monthlySavings * monthsActive).toFixed(2)}`);
      } else {
        // Canceled/downgraded: count from event date to now (minimum 1 month)
        const monthsActive = Math.max(1,
          Math.ceil((now - eventDate) / (1000 * 60 * 60 * 24 * 30))
        );

        lifetimeSavings += monthlySavings * monthsActive;
        console.log(`  ✅ ${event.event_type} "${event.subscription_name}": $${monthlySavings}/mo × ${monthsActive} months = $${(monthlySavings * monthsActive).toFixed(2)}`);
      }
    });

    console.log(`💵 [calculateLifetimeSavings] Total lifetime savings: $${lifetimeSavings.toFixed(2)}`);
    return parseFloat(lifetimeSavings.toFixed(2));
  } catch (error) {
    console.error('❌ [calculateLifetimeSavings] Error:', error);
    return 0;
  }
};

/**
 * Get savings breakdown by event type
 */
export const getSavingsBreakdown = async (userId) => {
  console.log('📊 [getSavingsBreakdown] Starting for user:', userId);

  try {
    const { data, error } = await supabase
      .from('savings_events')
      .select('*')
      .eq('user_id', userId);

    if (error) throw error;

    console.log(`📋 [getSavingsBreakdown] Found ${data?.length || 0} events`);

    const now = new Date();
    const breakdown = {
      canceled: { monthly: 0, lifetime: 0, count: 0 },
      downgraded: { monthly: 0, lifetime: 0, count: 0 },
      annual_switch: { monthly: 0, lifetime: 0, count: 0 }
    };

    data.forEach(event => {
      const eventDate = new Date(event.event_date);
      const monthlySavings = parseFloat(event.monthly_savings);
      const type = event.event_type;

      breakdown[type].count++;

      if (type === 'annual_switch') {
        const endDate = new Date(event.savings_end_date);

        // Monthly: only if still active
        if (endDate > now) {
          breakdown[type].monthly += monthlySavings;
        }

        // Lifetime: calculate months it was active (minimum 1)
        const actualEndDate = endDate < now ? endDate : now;
        const monthsActive = Math.max(1,
          Math.ceil((actualEndDate - eventDate) / (1000 * 60 * 60 * 24 * 30))
        );
        breakdown[type].lifetime += monthlySavings * monthsActive;
        console.log(`  ✅ Annual switch: $${monthlySavings}/mo × ${monthsActive} months = $${(monthlySavings * monthsActive).toFixed(2)}`);
      } else {
        // Canceled/downgraded: ongoing savings
        breakdown[type].monthly += monthlySavings;

        // Lifetime: months from event to now (minimum 1)
        const monthsActive = Math.max(1,
          Math.ceil((now - eventDate) / (1000 * 60 * 60 * 24 * 30))
        );
        breakdown[type].lifetime += monthlySavings * monthsActive;
        console.log(`  ✅ ${type}: $${monthlySavings}/mo × ${monthsActive} months = $${(monthlySavings * monthsActive).toFixed(2)}`);
      }
    });

    // Round all values
    Object.keys(breakdown).forEach(key => {
      breakdown[key].monthly = parseFloat(breakdown[key].monthly.toFixed(2));
      breakdown[key].lifetime = parseFloat(breakdown[key].lifetime.toFixed(2));
    });

    console.log('💵 [getSavingsBreakdown] Final breakdown:', breakdown);
    return breakdown;
  } catch (error) {
    console.error('❌ [getSavingsBreakdown] Error:', error);
    return {
      canceled: { monthly: 0, lifetime: 0, count: 0 },
      downgraded: { monthly: 0, lifetime: 0, count: 0 },
      annual_switch: { monthly: 0, lifetime: 0, count: 0 }
    };
  }
};

// ============================================================================
// SAVINGS EVENT RECORDING
// ============================================================================

/**
 * Record a new savings event
 */
export const recordSavingsEvent = async (
  userId,
  subscriptionId,
  subscriptionName,
  eventType,
  oldPrice,
  newPrice,
  notes = null
) => {
  console.log('📝 [recordSavingsEvent] Attempting to record:', {
    userId,
    subscriptionId,
    subscriptionName,
    eventType,
    oldPrice,
    newPrice
  });

  try {
    // Calculate monthly savings
    let monthlySavings = 0;
    let savingsEndDate = null;

    if (eventType === 'canceled') {
      monthlySavings = oldPrice; // Save the full amount
      console.log(`  💵 Canceled: $${oldPrice}/mo → $0 = $${monthlySavings}/mo saved`);
    } else if (eventType === 'downgraded') {
      monthlySavings = oldPrice - newPrice; // Save the difference
      console.log(`  💵 Downgraded: $${oldPrice}/mo → $${newPrice}/mo = $${monthlySavings}/mo saved`);
    } else if (eventType === 'annual_switch') {
      // oldPrice = monthly cost * 12, newPrice = annual price
      const oldAnnual = oldPrice;
      const newAnnual = newPrice;
      monthlySavings = (oldAnnual - newAnnual) / 12;

      // Set expiration date 12 months from now
      savingsEndDate = new Date();
      savingsEndDate.setMonth(savingsEndDate.getMonth() + 12);
      console.log(`  💵 Annual switch: $${oldPrice}/yr → $${newPrice}/yr = $${monthlySavings}/mo saved (expires ${savingsEndDate.toLocaleDateString()})`);
    }

    // Don't record if no savings (or negative savings)
    if (monthlySavings <= 0) {
      console.warn('⚠️  No positive savings to record:', monthlySavings);
      return null;
    }

    console.log('  💾 Inserting into database...');

    const { data, error } = await supabase
      .from('savings_events')
      .insert({
        user_id: userId,
        subscription_id: subscriptionId,
        subscription_name: subscriptionName,
        event_type: eventType,
        monthly_savings: monthlySavings,
        event_date: new Date().toISOString(),
        old_price: oldPrice,
        new_price: newPrice,
        savings_end_date: savingsEndDate ? savingsEndDate.toISOString() : null,
        notes
      })
      .select()
      .single();

    console.log('  📦 Insert response - data:', data);
    console.log('  📦 Insert response - error:', error);

    if (error) {
      console.error('❌ [recordSavingsEvent] Database error:', error);
      console.error('   Error code:', error.code);
      console.error('   Error message:', error.message);
      console.error('   Error details:', error.details);
      console.error('   Error hint:', error.hint);
      throw error;
    }

    console.log('✅ [recordSavingsEvent] Successfully recorded! Event ID:', data.id);

    return data;
  } catch (error) {
    console.error('❌ [recordSavingsEvent] Error:', error.message);
    if (error.message?.includes('relation') || error.message?.includes('does not exist')) {
      console.warn('⚠️  savings_events table does not exist. Run add_ai_insights_tables.sql in Supabase');
    }
    throw error;
  }
};

/**
 * Get all savings events for a user (for display/history)
 */
export const getSavingsEvents = async (userId, limit = 50) => {
  try {
    const { data, error } = await supabase
      .from('savings_events')
      .select('*')
      .eq('user_id', userId)
      .order('event_date', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching savings events:', error);
    return [];
  }
};

// ============================================================================
// USAGE CHECK-INS
// ============================================================================

/**
 * Record user's response to usage check-in
 */
export const recordUsageCheck = async (userId, subscriptionId, isUsing, userNote = null) => {
  try {
    const { data, error } = await supabase
      .from('subscription_usage_checks')
      .insert({
        user_id: userId,
        subscription_id: subscriptionId,
        check_date: new Date().toISOString(),
        is_using: isUsing,
        user_note: userNote
      })
      .select()
      .single();

    if (error) throw error;

    console.log('✅ Usage check recorded:', {
      subscription: subscriptionId,
      using: isUsing
    });

    return data;
  } catch (error) {
    console.error('Error recording usage check:', error);
    throw error;
  }
};

/**
 * Get usage check history for a subscription
 */
export const getUsageChecks = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('subscription_usage_checks')
      .select('*')
      .eq('user_id', userId)
      .order('check_date', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching usage checks:', error);
    return [];
  }
};

/**
 * Get subscriptions that haven't been checked in X days
 */
export const getSubscriptionsNeedingCheckIn = async (userId, daysSinceLastCheck = 90) => {
  try {
    // Get all user's active subscriptions
    const { data: subscriptions, error: subError } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active');

    if (subError) throw subError;

    // Get all usage checks
    const { data: checks, error: checkError } = await supabase
      .from('subscription_usage_checks')
      .select('*')
      .eq('user_id', userId);

    if (checkError) throw checkError;

    // Find subscriptions that haven't been checked recently
    const needingCheckIn = subscriptions.filter(sub => {
      const subChecks = checks.filter(c => c.subscription_id === sub.id);

      if (subChecks.length === 0) {
        // Never checked - check if subscription is older than daysSinceLastCheck
        const createdAt = new Date(sub.created_at);
        const now = new Date();
        const daysSinceCreation = (now - createdAt) / (1000 * 60 * 60 * 24);
        return daysSinceCreation >= daysSinceLastCheck;
      }

      // Get most recent check
      const lastCheck = new Date(Math.max(...subChecks.map(c => new Date(c.check_date))));
      const now = new Date();
      const daysSinceCheck = (now - lastCheck) / (1000 * 60 * 60 * 24);

      return daysSinceCheck >= daysSinceLastCheck;
    });

    return needingCheckIn;
  } catch (error) {
    console.error('Error getting subscriptions needing check-in:', error);
    return [];
  }
};

export default {
  calculateMonthlySavings,
  calculateLifetimeSavings,
  getSavingsBreakdown,
  recordSavingsEvent,
  getSavingsEvents,
  recordUsageCheck,
  getUsageChecks,
  getSubscriptionsNeedingCheckIn
};
